// Add/edit vendor form (right-side full-height sheet). The save goes through the page's
// location CONTEXT token (the vendor is created in — and can only be edited within — that
// operation unit); proof uploads use the NORMAL auth token.
//
// Bank-account rules mirrored from VendorController.apiSave:
//   - every non-empty row requires bank + account number + IFSC + an uploaded proof document;
//   - rows posted back WITHOUT their id get soft-deleted server-side, so existing ids are
//     always echoed back (removing a row here is what deletes it);
//   - exactly one row is default (radio-like toggle; the first row becomes default if none).

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileCheck2, Paperclip, Plus, Trash2, Upload } from "lucide-react";

import { ApiError } from "@/lib/api";
import {
  saveVendor,
  uploadVendorBankProof,
  type SaveVendorBankAccountPayload,
  type SaveVendorPayload,
  type VendorMap,
} from "@/lib/vendors";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

interface BankRow {
  /** Local list key (rows are added/removed freely). */
  key: string;
  /** Existing MstVendorBankAccount id (edit mode) — MUST be echoed back on save. */
  id?: number;
  bankId: number | null;
  bankAccount: string;
  bankBranch: string;
  ifscCode: string;
  accountHolderName: string;
  /** Server file name from uploadVendorBankProofFile — required before save. */
  proofFileName: string;
  isDefault: boolean;
}

let rowSeq = 0;
function newRow(partial?: Partial<BankRow>): BankRow {
  return {
    key: `row-${++rowSeq}`,
    bankId: null,
    bankAccount: "",
    bankBranch: "",
    ifscCode: "",
    accountHolderName: "",
    proofFileName: "",
    isDefault: false,
    ...partial,
  };
}

/** Mirrors the server's isBlankBankAccountRow: rows with an id are never blank. */
function rowIsEmpty(r: BankRow): boolean {
  return (
    r.id == null &&
    r.bankId == null &&
    !r.bankAccount.trim() &&
    !r.bankBranch.trim() &&
    !r.ifscCode.trim() &&
    !r.accountHolderName.trim() &&
    !r.proofFileName
  );
}

export function VendorFormSheet({
  open,
  onOpenChange,
  token,
  contextToken,
  vendor,
  banks,
  operationUnitName,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Normal auth token (proof uploads). */
  token: string;
  /** Location context token (the save itself). */
  contextToken: string | null;
  /** null = add mode. */
  vendor: VendorMap | null;
  banks: ComboboxOption[];
  operationUnitName: string | null;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [pan, setPan] = useState("");
  const [rows, setRows] = useState<BankRow[]>([newRow({ isDefault: true })]);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const errorsRef = useRef<HTMLDivElement>(null);

  // Prefill (edit) / reset (add) each time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setName(vendor?.name ?? "");
    setAddress(vendor?.address ?? "");
    setContactPerson(vendor?.contactPerson ?? "");
    setContactNumber(vendor?.contactNumber ?? "");
    setEmail(vendor?.email ?? "");
    setWhatsAppNumber(vendor?.whatsAppNumber ?? "");
    setPan(vendor?.pan ?? "");
    const existing = (vendor?.bankAccounts ?? []).map((a) =>
      newRow({
        id: a.id,
        bankId: a.bank?.id ?? null,
        bankAccount: a.bankAccount ?? "",
        bankBranch: a.bankBranch ?? "",
        ifscCode: a.ifscCode ?? "",
        accountHolderName: a.accountHolderName ?? "",
        proofFileName: a.verificationProofFileName ?? "",
        isDefault: a.isDefault,
      })
    );
    if (existing.length > 0 && !existing.some((r) => r.isDefault)) existing[0].isDefault = true;
    setRows(existing.length > 0 ? existing : [newRow({ isDefault: true })]);
    setFormErrors([]);
    setSaving(false);
    setUploadingKey(null);
    setUploadPct(null);
  }, [open, vendor]);

  const patchRow = (key: string, patch: Partial<BankRow>) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const makeDefault = (key: string) => {
    setRows((rs) => rs.map((r) => ({ ...r, isDefault: r.key === key })));
  };

  const removeRow = (key: string) => {
    setRows((rs) => {
      const next = rs.filter((r) => r.key !== key);
      if (next.length === 0) return [newRow({ isDefault: true })];
      if (!next.some((r) => r.isDefault)) next[0] = { ...next[0], isDefault: true };
      return next;
    });
  };

  const uploadProof = (key: string, file: File) => {
    setUploadingKey(key);
    setUploadPct(0);
    uploadVendorBankProof(token, file, (pct) => setUploadPct(pct))
      .then((r) => {
        if (r.errorMessage || !r.fileName) {
          toast.error(r.errorMessage || "Upload failed.");
          return;
        }
        patchRow(key, { proofFileName: r.fileName });
        toast.success(r.myMessage || "Proof document uploaded.");
      })
      .catch((e) => {
        toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Upload failed.");
      })
      .finally(() => {
        setUploadingKey(null);
        setUploadPct(null);
      });
  };

  const validate = (): string[] => {
    const errors: string[] = [];
    if (!name.trim()) errors.push("Enter the vendor name.");
    rows.forEach((r, i) => {
      if (rowIsEmpty(r)) return;
      const n = `Bank account ${i + 1}`;
      if (r.bankId == null) errors.push(`${n}: select a bank.`);
      if (!r.bankAccount.trim()) errors.push(`${n}: enter the account number.`);
      if (!r.ifscCode.trim()) errors.push(`${n}: enter the IFSC code.`);
      if (!r.proofFileName) errors.push(`${n}: upload the proof document.`);
    });
    return errors;
  };

  const showErrors = (errors: string[]) => {
    setFormErrors(errors);
    requestAnimationFrame(() =>
      errorsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const save = () => {
    if (!contextToken) {
      toast.error("Select a location first.");
      return;
    }
    const errors = validate();
    if (errors.length > 0) {
      showErrors(errors);
      return;
    }
    setFormErrors([]);

    const activeRows = rows.filter((r) => !rowIsEmpty(r));
    // Server rule mirrored: exactly one default — the first row when none is marked.
    const defaultKey = (activeRows.find((r) => r.isDefault) ?? activeRows[0])?.key ?? null;
    const bankAccounts: SaveVendorBankAccountPayload[] = activeRows.map((r) => ({
      ...(r.id != null ? { id: r.id } : {}),
      bank: { id: r.bankId as number },
      bankAccount: r.bankAccount.trim(),
      bankBranch: r.bankBranch.trim(),
      ifscCode: r.ifscCode.trim(),
      accountHolderName: r.accountHolderName.trim(),
      verificationProofFileName: r.proofFileName,
      isDefault: r.key === defaultKey,
    }));
    const defaultRow = activeRows.find((r) => r.key === defaultKey);
    const payload: SaveVendorPayload = {
      ...(vendor ? { id: vendor.id } : {}),
      name: name.trim(),
      address: address.trim(),
      contactPerson: contactPerson.trim(),
      contactNumber: contactNumber.trim(),
      email: email.trim(),
      whatsAppNumber: whatsAppNumber.trim(),
      pan: pan.trim(),
      bank: defaultRow?.bankId != null ? { id: defaultRow.bankId } : null,
      bankAccounts,
    };

    setSaving(true);
    saveVendor(contextToken, payload)
      .then((r) => {
        if (r.errorMessage) {
          const list = [r.errorMessage, ...(r.errors ? [String(r.errors)] : [])];
          showErrors(list);
          toast.error(r.errorMessage);
          return;
        }
        toast.success(r.myMessage || "Vendor saved.");
        onOpenChange(false);
        onSaved();
      })
      .catch((e) => {
        const msg =
          e instanceof ApiError || e instanceof Error ? e.message : "Could not save the vendor.";
        showErrors([msg]);
        toast.error(msg);
      })
      .finally(() => setSaving(false));
  };

  const busy = saving || uploadingKey !== null;

  return (
    <Sheet open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <SheetContent side="bottom" className="max-h-[90dvh] gap-0">
        <SheetHeader className="border-b border-border">
          <SheetTitle>{vendor ? "Edit vendor" : "Add vendor"}</SheetTitle>
          <SheetDescription>
            {operationUnitName
              ? `This vendor belongs to ${operationUnitName} and can only be paid there.`
              : "Vendors can only be paid within their own location."}
          </SheetDescription>
        </SheetHeader>

        {/* [&>*]:shrink-0 is load-bearing: without it the flex children compress instead of
            overflowing, and the spilled content (bank account fields) becomes unreachable. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 [&>*]:shrink-0">
          {formErrors.length > 0 && (
            <div
              ref={errorsRef}
              className="scroll-mt-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              <ul className="flex list-disc flex-col gap-0.5 pl-4">
                {formErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="vendor-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="vendor-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vendor name"
              disabled={busy}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="vendor-address">Address</Label>
            <Textarea
              id="vendor-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              disabled={busy}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vendor-contact-person">Contact person</Label>
              <Input
                id="vendor-contact-person"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vendor-contact-number">Contact number</Label>
              <Input
                id="vendor-contact-number"
                type="tel"
                inputMode="tel"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vendor-email">Email</Label>
              <Input
                id="vendor-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vendor-whatsapp">WhatsApp number</Label>
              <Input
                id="vendor-whatsapp"
                type="tel"
                inputMode="tel"
                value={whatsAppNumber}
                onChange={(e) => setWhatsAppNumber(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vendor-pan">PAN</Label>
              <Input
                id="vendor-pan"
                value={pan}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
                placeholder="AAAAA9999A"
                className="uppercase"
                disabled={busy}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Bank accounts</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setRows((rs) => [...rs, newRow({ isDefault: rs.length === 0 })])}
            >
              <Plus /> Add account
            </Button>
          </div>
          <p className="-mt-3 text-xs text-muted-foreground">
            Each account needs a bank, account number, IFSC and an uploaded proof document.
            Removing an account here deletes it on save.
          </p>

          {rows.map((row, i) => (
            <BankAccountRow
              key={row.key}
              index={i}
              row={row}
              banks={banks}
              disabled={busy}
              uploading={uploadingKey === row.key}
              uploadPct={uploadingKey === row.key ? uploadPct : null}
              onPatch={(patch) => patchRow(row.key, patch)}
              onMakeDefault={() => makeDefault(row.key)}
              onRemove={() => removeRow(row.key)}
              onUpload={(file) => uploadProof(row.key, file)}
            />
          ))}
        </div>

        <SheetFooter className="flex-row border-t border-border">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" className="flex-1" disabled={busy || !contextToken} onClick={save}>
            {saving ? "Saving…" : vendor ? "Save changes" : "Save vendor"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function BankAccountRow({
  index,
  row,
  banks,
  disabled,
  uploading,
  uploadPct,
  onPatch,
  onMakeDefault,
  onRemove,
  onUpload,
}: {
  index: number;
  row: BankRow;
  banks: ComboboxOption[];
  disabled: boolean;
  uploading: boolean;
  uploadPct: number | null;
  onPatch: (patch: Partial<BankRow>) => void;
  onMakeDefault: () => void;
  onRemove: () => void;
  onUpload: (file: File) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <Card size="sm">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm font-medium">
            Account {index + 1}
            {row.isDefault ? (
              <Badge>Default</Badge>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={disabled}
                onClick={onMakeDefault}
              >
                Make default
              </Button>
            )}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-destructive"
            aria-label={`Remove account ${index + 1}`}
            disabled={disabled}
            onClick={onRemove}
          >
            <Trash2 />
          </Button>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>
            Bank <span className="text-destructive">*</span>
          </Label>
          <Combobox
            options={banks}
            value={row.bankId}
            onValueChange={(id) => onPatch({ bankId: id })}
            placeholder="Select bank…"
            disabled={disabled}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${row.key}-account`}>
              Account number <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${row.key}-account`}
              inputMode="numeric"
              value={row.bankAccount}
              onChange={(e) => onPatch({ bankAccount: e.target.value })}
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${row.key}-ifsc`}>
              IFSC <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${row.key}-ifsc`}
              value={row.ifscCode}
              onChange={(e) => onPatch({ ifscCode: e.target.value.toUpperCase() })}
              className="uppercase"
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${row.key}-branch`}>Branch</Label>
            <Input
              id={`${row.key}-branch`}
              value={row.bankBranch}
              onChange={(e) => onPatch({ bankBranch: e.target.value })}
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${row.key}-holder`}>Account holder name</Label>
            <Input
              id={`${row.key}-holder`}
              value={row.accountHolderName}
              onChange={(e) => onPatch({ accountHolderName: e.target.value })}
              disabled={disabled}
            />
          </div>
        </div>

        {/* Proof document — required before save for any non-empty row. */}
        <div className="flex flex-col gap-1.5">
          <Label>
            Proof document <span className="text-destructive">*</span>
          </Label>
          {row.proofFileName && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileCheck2 className="size-3.5 shrink-0 text-green-700 dark:text-green-400" />
              <span className="truncate">{row.proofFileName}</span>
            </span>
          )}
          {uploading && (
            <div className="flex items-center gap-2">
              <Progress value={uploadPct} className="flex-1" />
              <span className="w-9 text-right text-xs text-muted-foreground">
                {uploadPct == null ? "…" : `${uploadPct}%`}
              </span>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn("self-start", !row.proofFileName && "border-dashed")}
            disabled={disabled || uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {row.proofFileName ? <Paperclip /> : <Upload />}
            {row.proofFileName ? "Replace document" : "Upload document"}
          </Button>
          {/* Hidden picker behind the styled button — no visible native control. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = "";
              if (file) onUpload(file);
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
